/**
 * GpioCounterBridge — BrightSign GPIO people counter bridge.
 *
 * The BrightScript side (`gpio-sensor.brs`) owns the rising-edge debounce
 * logic and emits one aggregated audienceSignals envelope every 60s (or
 * earlier if enough edges accumulate). Each envelope looks like:
 *
 *   {
 *     type: 'audienceSignals',
 *     source: 'gpio-counter',
 *     delta:  <int, edges since last emission>,
 *     total:  <int, lifetime edge count since boot>,
 *     perPin: { '0': n0, '1': n1, ... },
 *     debounceMs: <int>,
 *     intervalMs: <int, wall-clock ms since last emission>,
 *     t: <ms, UpTime-based>
 *   }
 *
 * This JS bridge receives those envelopes, re-stamps them with
 * `fingerprint` + `screenId`, translates `delta` into an occupancy estimate,
 * and re-emits through the existing Socket.IO `audienceSignals` path so the
 * backend's `socket.js` handler processes them identically to
 * face-detection-derived signals.
 *
 * Only active when `window.BSDeviceInfo` is present AND the remote flag
 * `window.__TRILLBOARDS_BRIGHTSIGN_PERIPHERALS_DISABLED` is not true.
 */

/* eslint-disable no-console */

const BRIDGE_NAME = 'GpioCounterBridge';

// Backend rate-limiter is 1 audienceSignals / 5s / fingerprint. We enforce
// a matching minimum here so a misconfigured GPIO pin (e.g. stuck high at
// 10 Hz) cannot flood the socket.
const MIN_EMIT_INTERVAL_MS = 5000;

// A "busy minute" threshold for the emit window: when delta >= this many
// edges in the emission window we treat the scene as crowded. Matches the
// CTV audience-counter heuristic so analytics dashboards compare cleanly.
const BUSY_DELTA_THRESHOLD = 8;

let _installed = false;
// Sentinel: Number.NEGATIVE_INFINITY means "never emitted". First call
// always passes the rate-limit guard; each successful emit overwrites this
// with the current nowMs.
let _lastEmitMs = Number.NEGATIVE_INFINITY;

const _counters = {
  received: 0,
  emitted: 0,
  droppedInvalid: 0,
  droppedRateLimited: 0,
  droppedNoSocket: 0,
};

function coerceInt(value, fallback, min, max) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  const rounded = Math.round(n);
  if (typeof min === 'number' && rounded < min) return min;
  if (typeof max === 'number' && rounded > max) return max;
  return rounded;
}

function parseIncomingMessage(raw) {
  if (raw == null) return null;
  if (typeof raw === 'object') return raw;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch (_) {
      return null;
    }
  }
  return null;
}

/**
 * Is this BSMessagePort payload a gpio-counter audience signal we care
 * about? Anything that doesn't match is forwarded unchanged to the next
 * handler in the chain (no swallowing other bridges' traffic).
 */
function isValidGpioCounterSignal(msg) {
  if (!msg || typeof msg !== 'object') return false;
  if (msg.type !== 'audienceSignals') return false;
  if (msg.source !== 'gpio-counter') return false;
  const delta = Number(msg.delta);
  if (!Number.isFinite(delta) || delta < 0) return false;
  return true;
}

function resolveFingerprint() {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      return window.localStorage.getItem('trillboard_fingerprint') || null;
    }
  } catch (_) {
    /* ignore */
  }
  return null;
}

function resolveSocket() {
  if (typeof window === 'undefined') return null;
  return window.trillboardsSocket || null;
}

function resolveScreenId() {
  const sock = resolveSocket();
  if (sock && sock.screenId) return sock.screenId;
  if (
    typeof window !== 'undefined' &&
    window.__TRILLBOARD_CONFIG__ &&
    window.__TRILLBOARD_CONFIG__.screenId
  ) {
    return window.__TRILLBOARD_CONFIG__.screenId;
  }
  return null;
}

/**
 * Build the canonical audienceSignals payload from a GPIO counter envelope.
 *
 * A GPIO people counter is a one-dimensional sensor — it tells us how many
 * bodies crossed the threshold in the last window, not how many are looking
 * at the screen right now. We treat each crossing as a "potential audience"
 * observation with:
 *
 *   avgFaceCount / maxFaceCount = min(delta, 100)
 *   avgAttention                = 0.5 (no actual attention signal)
 *   adReceptivityScore          = 0.5 (neutral — we do not know)
 *   measurementQuality          = 'gpio_counter'
 *   evidenceGrade               = 'proxy_only'  (no face, no CSI)
 *
 * This keeps the audience counting path alive without over-claiming the
 * observation quality to the backend VAS / FEIN enrichment layer.
 */
function buildAudienceSignalsPayload(msg, context) {
  const { fingerprint, screenId } = context;
  const delta = coerceInt(msg.delta, 0, 0, 1e6);
  const total = coerceInt(msg.total, 0, 0, 1e9);
  const intervalMs = coerceInt(msg.intervalMs, 60000, 1000, 3600000);
  const debounceMs = coerceInt(msg.debounceMs, 100, 10, 5000);

  // Crowded percentage — how much of the window was "busy"? Use delta per
  // minute as the rough divisor so a 60-edge minute saturates to 1.0.
  const edgesPerMinute = (delta / Math.max(1, intervalMs)) * 60000;
  const crowdedPct = Math.min(1, edgesPerMinute / 60);
  const faceCount = Math.min(delta, 100);

  return {
    fingerprint,
    screenId,
    avgFaceCount: faceCount,
    maxFaceCount: faceCount,
    // GPIO gives us no real attention signal, so 0.5 is the honest middle.
    avgAttention: 0.5,
    adReceptivityScore: 0.5,
    crowdedPct,
    gpioDelta: delta,
    gpioTotal: total,
    gpioPerPin: (msg && msg.perPin && typeof msg.perPin === 'object') ? msg.perPin : {},
    gpioDebounceMs: debounceMs,
    timestamp: Date.now(),
    windowStart: Date.now() - intervalMs,
    windowEnd: Date.now(),
    source: 'gpio-counter',
    ingestSource: 'brightsign_gpio_counter',
    sensingIntervalMs: intervalMs,
    avgDwellTimeMs: 0,
    measurementQuality: 'gpio_counter',
    observationFamily: delta > 0 ? 'screen_audience' : 'empty_scene',
    // GPIO is a proxy sensor — no camera, no CSI. We never claim
    // face_backed for GPIO data.
    evidenceGrade: delta > 0 ? 'proxy_only' : 'empty',
    decisionability: delta >= BUSY_DELTA_THRESHOLD ? 'decisionable' : 'non_decisionable',
    decisionBlockReasons: delta >= BUSY_DELTA_THRESHOLD
      ? []
      : ['gpio_below_threshold'],
  };
}

/**
 * Handle one GPIO counter envelope. Returns one of:
 *   'emitted', 'dropped_invalid', 'dropped_rate_limit', 'dropped_no_socket',
 *   'ignored' (wrong message type — not an error, just someone else's msg)
 */
function handleGpioCounterSignal(rawMessage, { nowMs } = {}) {
  const msg = parseIncomingMessage(rawMessage);
  if (!msg || msg.type !== 'audienceSignals' || msg.source !== 'gpio-counter') {
    return 'ignored';
  }
  _counters.received += 1;

  if (!isValidGpioCounterSignal(msg)) {
    _counters.droppedInvalid += 1;
    return 'dropped_invalid';
  }

  const fingerprint = resolveFingerprint();
  if (!fingerprint) {
    _counters.droppedNoSocket += 1;
    return 'dropped_no_socket';
  }

  const socket = resolveSocket();
  if (!socket || typeof socket.emit !== 'function') {
    _counters.droppedNoSocket += 1;
    return 'dropped_no_socket';
  }

  const now = typeof nowMs === 'number' ? nowMs : Date.now();
  if (now - _lastEmitMs < MIN_EMIT_INTERVAL_MS) {
    _counters.droppedRateLimited += 1;
    return 'dropped_rate_limit';
  }

  const screenId = resolveScreenId();
  const payload = buildAudienceSignalsPayload(msg, { fingerprint, screenId });

  try {
    socket.emit('audienceSignals', payload);
    _counters.emitted += 1;
    _lastEmitMs = now;
    return 'emitted';
  } catch (err) {
    _counters.droppedNoSocket += 1;
    return 'dropped_no_socket';
  }
}

/**
 * Install the BSMessagePort handler. Idempotent. Composes with any
 * existing handler so the SensorPodBridge + GpioCounterBridge can coexist
 * on the same port.
 */
function init() {
  if (typeof window === 'undefined') return false;
  if (_installed) return true;

  if (typeof window.BSDeviceInfo === 'undefined') return false;
  if (window.__TRILLBOARDS_BRIGHTSIGN_PERIPHERALS_DISABLED === true) {
    console.log('[' + BRIDGE_NAME + '] disabled by flag; skipping install');
    return false;
  }

  const port = window.BSMessagePort;
  if (!port) return false;

  const handler = (rawMessage) => {
    try {
      let data = rawMessage;
      if (rawMessage && typeof rawMessage === 'object' && 'detail' in rawMessage) {
        data = rawMessage.detail && rawMessage.detail.message
          ? rawMessage.detail.message
          : rawMessage.detail;
      }
      handleGpioCounterSignal(data);
    } catch (err) {
      try {
        console.error('[' + BRIDGE_NAME + '] handler error', err);
      } catch (_) { /* noop */ }
    }
  };

  if (typeof port.addEventListener === 'function') {
    port.addEventListener('bsmessage', handler);
  } else {
    const prior = port.onbsmessage;
    port.onbsmessage = function (msg) {
      if (typeof prior === 'function') {
        try {
          prior(msg);
        } catch (err) {
          try {
            console.error('[' + BRIDGE_NAME + '] prior handler error', err);
          } catch (_) { /* noop */ }
        }
      }
      handler(msg);
    };
  }

  _installed = true;
  try {
    window.__TRILL_BS_GPIO_COUNTER_BRIDGE__ = true;
    console.log('[' + BRIDGE_NAME + '] installed');
  } catch (_) { /* ignore */ }
  return true;
}

function __resetForTests() {
  _installed = false;
  _lastEmitMs = Number.NEGATIVE_INFINITY;
  _counters.received = 0;
  _counters.emitted = 0;
  _counters.droppedInvalid = 0;
  _counters.droppedRateLimited = 0;
  _counters.droppedNoSocket = 0;
  if (typeof window !== 'undefined') {
    try {
      delete window.__TRILL_BS_GPIO_COUNTER_BRIDGE__;
    } catch (_) {
      window.__TRILL_BS_GPIO_COUNTER_BRIDGE__ = undefined;
    }
  }
}

const __getCountersForTests = () => ({ ..._counters });

export {
  init,
  handleGpioCounterSignal,
  buildAudienceSignalsPayload,
  isValidGpioCounterSignal,
  parseIncomingMessage,
  coerceInt,
  __resetForTests,
  __getCountersForTests,
  MIN_EMIT_INTERVAL_MS,
  BUSY_DELTA_THRESHOLD,
  BRIDGE_NAME,
};
